import React, { useEffect, useMemo, useState } from "react";
import { getAdminCourses } from "../../../api/coursesAdmin";
import {
  createAdminTraining,
  deleteAdminTraining,
  getAdminTrainings,
  removeAdminTrainingTrainerAvatar,
  updateAdminTraining,
  uploadAdminTrainingTrainerAvatar,
} from "../../../api/trainingsAdmin";
import { useNotification } from "../../../context/NotificationContext";
import { resolveMediaUrl } from "../../../utils/media";

const EMPTY_FORM = {
  title: "",
  description: "",
  benefits: [{ text: "" }],
  registerNowText: "Register now",
  downloadBrochureText: "Download brochure",
  trainerSectionTitle: "Meet the trainer",
  trainerRole: "Trainer",
  trainerName: "",
  trainerBio: "",
  trainerAvatarImageUrl: "",
  trainerHighlights: [{ text: "" }],
  courseInfoTitle: "Course information",
  courseInfoSubtitle: "",
  dateText: "",
  locationText: "",
  formatText: "",
  languageText: "",
  seatsRemainingText: "",
  contactText: "",
  isActive: true,
  displayOrder: 1,
};

const mapTrainingToForm = (training) => ({
  title: training?.title || "",
  description: training?.description || "",
  benefits: Array.isArray(training?.benefits)
    ? training.benefits.map((x) => ({ text: x?.text || "" }))
    : [{ text: "" }],
  registerNowText: training?.registerNowText || "Register now",
  downloadBrochureText: training?.downloadBrochureText || "Download brochure",
  trainerSectionTitle: training?.trainerSectionTitle || "Meet the trainer",
  trainerRole: training?.trainerRole || "Trainer",
  trainerName: training?.trainerName || "",
  trainerBio: training?.trainerBio || "",
  trainerAvatarImageUrl: training?.trainerAvatarImageUrl || "",
  trainerHighlights: Array.isArray(training?.trainerHighlights)
    ? training.trainerHighlights.map((x) => ({ text: x?.text || "" }))
    : [{ text: "" }],
  courseInfoTitle: training?.courseInfoTitle || "Course information",
  courseInfoSubtitle: training?.courseInfoSubtitle || "",
  dateText: training?.dateText || "",
  locationText: training?.locationText || "",
  formatText: training?.formatText || "",
  languageText: training?.languageText || "",
  seatsRemainingText: training?.seatsRemainingText || "",
  contactText: training?.contactText || "",
  isActive: typeof training?.isActive === "boolean" ? training.isActive : true,
  displayOrder: Number(training?.displayOrder || 1),
});

const toPayload = (form) => ({
  title: String(form.title || "").trim(),
  description: String(form.description || "").trim(),
  benefits: (form.benefits || [])
    .map((x) => ({ text: String(x?.text || "").trim() }))
    .filter((x) => x.text),
  registerNowText: String(form.registerNowText || "").trim(),
  downloadBrochureText: String(form.downloadBrochureText || "").trim(),
  trainerSectionTitle: String(form.trainerSectionTitle || "").trim(),
  trainerRole: String(form.trainerRole || "").trim(),
  trainerName: String(form.trainerName || "").trim(),
  trainerBio: String(form.trainerBio || "").trim(),
  trainerAvatarImageUrl: String(form.trainerAvatarImageUrl || "").trim() || null,
  trainerHighlights: (form.trainerHighlights || [])
    .map((x) => ({ text: String(x?.text || "").trim() }))
    .filter((x) => x.text),
  courseInfoTitle: String(form.courseInfoTitle || "").trim(),
  courseInfoSubtitle: String(form.courseInfoSubtitle || "").trim(),
  dateText: String(form.dateText || "").trim(),
  locationText: String(form.locationText || "").trim(),
  formatText: String(form.formatText || "").trim(),
  languageText: String(form.languageText || "").trim(),
  seatsRemainingText: String(form.seatsRemainingText || "").trim(),
  contactText: String(form.contactText || "").trim(),
  isActive: Boolean(form.isActive),
  displayOrder: Number(form.displayOrder || 1),
});

const TrainingsManager = () => {
  const { notify } = useNotification();
  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [createAvatarFile, setCreateAvatarFile] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editAvatarFile, setEditAvatarFile] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const loadCourses = async () => {
      try {
        const data = await getAdminCourses();
        if (!isMounted) return;
        const normalized = Array.isArray(data) ? data : [];
        setCourses(normalized);
        if (!selectedCourseId && normalized.length) setSelectedCourseId(String(normalized[0].id));
      } catch (error) {
        notify({
          type: "error",
          title: "Failed to load courses",
          message: error?.response?.data?.message || error?.message || "",
        });
      }
    };
    loadCourses();
    return () => {
      isMounted = false;
    };
  }, [notify]);

  const loadItems = async (courseIdArg = selectedCourseId) => {
    const courseId = Number(courseIdArg);
    if (!Number.isFinite(courseId) || courseId <= 0) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const data = await getAdminTrainings({ courseId });
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      notify({
        type: "error",
        title: "Failed to load trainings",
        message: error?.response?.data?.message || error?.message || "",
      });
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourseId]);

  const sortedItems = useMemo(
    () =>
      [...items].sort(
        (a, b) =>
          Number(a?.displayOrder || 0) - Number(b?.displayOrder || 0) ||
          Number(a?.id || 0) - Number(b?.id || 0)
      ),
    [items]
  );

  const renderTextList = (rows, setRows, addLabel = "Add item") => (
    <div className="rounded-xl border border-slate-200 p-3 sm:col-span-2">
      {rows.map((row, idx) => (
        <div key={idx} className="flex gap-2 mb-2">
          <input
            type="text"
            value={row?.text || ""}
            onChange={(e) => {
              const next = [...rows];
              next[idx] = { ...(next[idx] || {}), text: e.target.value };
              setRows(next);
            }}
            className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={() => {
              const next = rows.filter((_, i) => i !== idx);
              setRows(next.length ? next : [{ text: "" }]);
            }}
            className="px-3 rounded border border-rose-200 text-rose-700"
          >
            -
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => setRows([...(rows || []), { text: "" }])}
        className="text-xs px-3 py-1.5 rounded border border-slate-300"
      >
        {addLabel}
      </button>
    </div>
  );

  const handleCreate = async () => {
    const courseId = Number(selectedCourseId);
    if (!Number.isFinite(courseId) || courseId <= 0) return notify({ type: "error", title: "Select a course first" });
    if (!String(createForm.title || "").trim()) return notify({ type: "error", title: "Training title is required" });
    setCreating(true);
    try {
      const created = await createAdminTraining({ courseId, payload: toPayload(createForm) });
      if (createAvatarFile && created?.id) {
        await uploadAdminTrainingTrainerAvatar({ courseId, trainingId: created.id, file: createAvatarFile });
      }
      notify({ type: "success", title: "Training created" });
      setCreateForm(EMPTY_FORM);
      setCreateAvatarFile(null);
      await loadItems(courseId);
    } catch (error) {
      notify({ type: "error", title: "Create failed", message: error?.response?.data?.message || error?.message || "" });
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm(mapTrainingToForm(item));
    setEditAvatarFile(null);
  };

  const saveEdit = async () => {
    const courseId = Number(selectedCourseId);
    if (!editingId || !Number.isFinite(courseId) || courseId <= 0) return;
    if (!String(editForm.title || "").trim()) return notify({ type: "error", title: "Training title is required" });
    try {
      await updateAdminTraining({ courseId, trainingId: editingId, payload: toPayload(editForm) });
      if (editAvatarFile) {
        await uploadAdminTrainingTrainerAvatar({ courseId, trainingId: editingId, file: editAvatarFile });
      }
      notify({ type: "success", title: "Training updated" });
      setEditingId(null);
      setEditForm(EMPTY_FORM);
      setEditAvatarFile(null);
      await loadItems(courseId);
    } catch (error) {
      notify({ type: "error", title: "Update failed", message: error?.response?.data?.message || error?.message || "" });
    }
  };

  const handleDelete = async (item) => {
    const courseId = Number(selectedCourseId);
    if (!Number.isFinite(courseId) || courseId <= 0) return;
    if (!window.confirm(`Delete training "${item?.title || item?.id}"?`)) return;
    try {
      await deleteAdminTraining({ courseId, trainingId: item.id });
      notify({ type: "success", title: "Training deleted" });
      await loadItems(courseId);
    } catch (error) {
      notify({ type: "error", title: "Delete failed", message: error?.response?.data?.message || error?.message || "" });
    }
  };

  return (
    <section className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#011337]">Trainings Manager</h2>
            <p className="text-sm text-slate-500 mt-1">CRUD trainings under each course and manage trainer avatar.</p>
          </div>
          <button type="button" onClick={() => loadItems()} className="px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm">
            Reload
          </button>
        </div>

        <div className="mb-4 max-w-[420px]">
          <label className="text-sm text-slate-700">
            Select Course
            <select
              value={selectedCourseId}
              onChange={(e) => {
                setSelectedCourseId(e.target.value);
                setEditingId(null);
              }}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
            >
              {!courses.length ? <option value="">No courses available</option> : null}
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  #{course.id} - {course.title || course.courseKey || "Untitled course"}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-sm text-slate-700 sm:col-span-2">Title
            <input type="text" value={createForm.title} onChange={(e) => setCreateForm((p) => ({ ...p, title: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </label>
          <label className="text-sm text-slate-700 sm:col-span-2">Description
            <textarea value={createForm.description} onChange={(e) => setCreateForm((p) => ({ ...p, description: e.target.value }))} className="mt-1 w-full min-h-[90px] rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </label>
          {renderTextList(createForm.benefits, (next) => setCreateForm((p) => ({ ...p, benefits: next })), "Add benefit")}
          {renderTextList(createForm.trainerHighlights, (next) => setCreateForm((p) => ({ ...p, trainerHighlights: next })), "Add trainer highlight")}
          <label className="text-sm text-slate-700">Register Now Text
            <input type="text" value={createForm.registerNowText} onChange={(e) => setCreateForm((p) => ({ ...p, registerNowText: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </label>
          <label className="text-sm text-slate-700">Download Brochure Text
            <input type="text" value={createForm.downloadBrochureText} onChange={(e) => setCreateForm((p) => ({ ...p, downloadBrochureText: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </label>
          <label className="text-sm text-slate-700">Trainer Section Title
            <input type="text" value={createForm.trainerSectionTitle} onChange={(e) => setCreateForm((p) => ({ ...p, trainerSectionTitle: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </label>
          <label className="text-sm text-slate-700">Trainer Role
            <input type="text" value={createForm.trainerRole} onChange={(e) => setCreateForm((p) => ({ ...p, trainerRole: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </label>
          <label className="text-sm text-slate-700">Trainer Name
            <input type="text" value={createForm.trainerName} onChange={(e) => setCreateForm((p) => ({ ...p, trainerName: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </label>
          <label className="text-sm text-slate-700 sm:col-span-2">Trainer Bio
            <textarea value={createForm.trainerBio} onChange={(e) => setCreateForm((p) => ({ ...p, trainerBio: e.target.value }))} className="mt-1 w-full min-h-[70px] rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </label>
          <label className="text-sm text-slate-700">Trainer Avatar URL
            <input type="text" value={createForm.trainerAvatarImageUrl} onChange={(e) => setCreateForm((p) => ({ ...p, trainerAvatarImageUrl: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </label>
          <label className="text-sm text-slate-700">Trainer Avatar File (optional)
            <input type="file" accept="image/*" onChange={(e) => setCreateAvatarFile(e.target.files?.[0] || null)} className="mt-1 block w-full text-sm" />
            {createAvatarFile?.name ? <span className="mt-1 block text-xs text-slate-500">Selected: {createAvatarFile.name}</span> : null}
          </label>
          <label className="text-sm text-slate-700">Course Info Title
            <input type="text" value={createForm.courseInfoTitle} onChange={(e) => setCreateForm((p) => ({ ...p, courseInfoTitle: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </label>
          <label className="text-sm text-slate-700">Course Info Subtitle
            <input type="text" value={createForm.courseInfoSubtitle} onChange={(e) => setCreateForm((p) => ({ ...p, courseInfoSubtitle: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </label>
          <label className="text-sm text-slate-700">Date Text
            <input type="text" value={createForm.dateText} onChange={(e) => setCreateForm((p) => ({ ...p, dateText: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </label>
          <label className="text-sm text-slate-700">Location Text
            <input type="text" value={createForm.locationText} onChange={(e) => setCreateForm((p) => ({ ...p, locationText: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </label>
          <label className="text-sm text-slate-700">Format Text
            <input type="text" value={createForm.formatText} onChange={(e) => setCreateForm((p) => ({ ...p, formatText: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </label>
          <label className="text-sm text-slate-700">Language Text
            <input type="text" value={createForm.languageText} onChange={(e) => setCreateForm((p) => ({ ...p, languageText: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </label>
          <label className="text-sm text-slate-700">Seats Remaining Text
            <input type="text" value={createForm.seatsRemainingText} onChange={(e) => setCreateForm((p) => ({ ...p, seatsRemainingText: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </label>
          <label className="text-sm text-slate-700">Contact Text
            <input type="text" value={createForm.contactText} onChange={(e) => setCreateForm((p) => ({ ...p, contactText: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </label>
          <label className="text-sm text-slate-700">Display Order
            <input type="number" value={createForm.displayOrder} onChange={(e) => setCreateForm((p) => ({ ...p, displayOrder: Number(e.target.value || 1) }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 mt-6">
            <input type="checkbox" checked={Boolean(createForm.isActive)} onChange={(e) => setCreateForm((p) => ({ ...p, isActive: e.target.checked }))} />
            Active
          </label>
        </div>

        <div className="mt-3 flex justify-end">
          <button type="button" disabled={creating} onClick={handleCreate} className="px-4 py-2 rounded-lg bg-[#FE7E3F] text-white hover:bg-[#FE7E3F]/90 text-sm disabled:opacity-60">
            {creating ? "Creating..." : "Create training"}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white">
        <table className="min-w-full table-fixed">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">ID</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Title</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Order</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Avatar</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-3 py-4 text-sm text-slate-500">Loading trainings...</td></tr>
            ) : sortedItems.length ? (
              sortedItems.map((item) => {
                const avatar = resolveMediaUrl(item?.trainerAvatarImageUrl);
                return (
                  <tr key={item.id} className="border-b border-slate-200 align-top">
                    <td className="px-3 py-3 text-sm text-slate-700">{item.id}</td>
                    <td className="px-3 py-3 text-sm font-medium text-slate-900">{item.title || "-"}</td>
                    <td className="px-3 py-3 text-sm text-slate-700">{item.displayOrder ?? "-"}</td>
                    <td className="px-3 py-3 text-sm">
                      <span className={`px-2 py-1 rounded text-xs ${item.isActive ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`}>{item.isActive ? "Active" : "Inactive"}</span>
                    </td>
                    <td className="px-3 py-3">{avatar ? <img src={avatar} alt="" className="w-16 h-16 object-cover rounded-full border border-slate-200" /> : <p className="text-xs text-slate-500">No avatar</p>}</td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col gap-2">
                        <button type="button" onClick={() => startEdit(item)} className="text-xs px-2 py-1.5 rounded border border-[#011337] text-[#011337] hover:bg-[#011337]/10">Edit</button>
                        <button type="button" onClick={() => handleDelete(item)} className="text-xs px-2 py-1.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50">Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr><td colSpan={6} className="px-3 py-4 text-sm text-slate-500">No trainings found for selected course.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editingId ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className="text-lg font-semibold text-[#011337]">Edit Training #{editingId}</h3>
            <button type="button" onClick={() => { setEditingId(null); setEditForm(EMPTY_FORM); setEditAvatarFile(null); }} className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50">Close</button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-sm text-slate-700 sm:col-span-2">Title
              <input type="text" value={editForm.title} onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="text-sm text-slate-700 sm:col-span-2">Description
              <textarea value={editForm.description} onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))} className="mt-1 w-full min-h-[90px] rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </label>
            {renderTextList(editForm.benefits, (next) => setEditForm((p) => ({ ...p, benefits: next })), "Add benefit")}
            {renderTextList(editForm.trainerHighlights, (next) => setEditForm((p) => ({ ...p, trainerHighlights: next })), "Add trainer highlight")}
            <label className="text-sm text-slate-700">Register Now Text
              <input type="text" value={editForm.registerNowText} onChange={(e) => setEditForm((p) => ({ ...p, registerNowText: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="text-sm text-slate-700">Download Brochure Text
              <input type="text" value={editForm.downloadBrochureText} onChange={(e) => setEditForm((p) => ({ ...p, downloadBrochureText: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="text-sm text-slate-700">Trainer Section Title
              <input type="text" value={editForm.trainerSectionTitle} onChange={(e) => setEditForm((p) => ({ ...p, trainerSectionTitle: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="text-sm text-slate-700">Trainer Role
              <input type="text" value={editForm.trainerRole} onChange={(e) => setEditForm((p) => ({ ...p, trainerRole: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="text-sm text-slate-700">Trainer Name
              <input type="text" value={editForm.trainerName} onChange={(e) => setEditForm((p) => ({ ...p, trainerName: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="text-sm text-slate-700 sm:col-span-2">Trainer Bio
              <textarea value={editForm.trainerBio} onChange={(e) => setEditForm((p) => ({ ...p, trainerBio: e.target.value }))} className="mt-1 w-full min-h-[70px] rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="text-sm text-slate-700">Trainer Avatar URL
              <input type="text" value={editForm.trainerAvatarImageUrl} onChange={(e) => setEditForm((p) => ({ ...p, trainerAvatarImageUrl: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="text-sm text-slate-700">Trainer Avatar File (optional)
              <input type="file" accept="image/*" onChange={(e) => setEditAvatarFile(e.target.files?.[0] || null)} className="mt-1 block w-full text-sm" />
              {editAvatarFile?.name ? <span className="mt-1 block text-xs text-slate-500">Selected: {editAvatarFile.name}</span> : null}
            </label>
            <div className="sm:col-span-2 flex items-center gap-2">
              <button type="button" onClick={async () => {
                const courseId = Number(selectedCourseId);
                if (!editAvatarFile || !editingId || !Number.isFinite(courseId) || courseId <= 0) return;
                try {
                  await uploadAdminTrainingTrainerAvatar({ courseId, trainingId: editingId, file: editAvatarFile });
                  notify({ type: "success", title: "Avatar uploaded" });
                  setEditAvatarFile(null);
                  await loadItems(courseId);
                } catch (error) {
                  notify({ type: "error", title: "Upload failed", message: error?.response?.data?.message || error?.message || "" });
                }
              }} disabled={!editAvatarFile} className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-60">Upload/Replace avatar</button>
              <button type="button" onClick={async () => {
                const courseId = Number(selectedCourseId);
                if (!editingId || !Number.isFinite(courseId) || courseId <= 0) return;
                try {
                  await removeAdminTrainingTrainerAvatar({ courseId, trainingId: editingId });
                  notify({ type: "success", title: "Avatar removed" });
                  await loadItems(courseId);
                } catch (error) {
                  notify({ type: "error", title: "Remove failed", message: error?.response?.data?.message || error?.message || "" });
                }
              }} className="text-xs px-3 py-1.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50">Remove avatar</button>
            </div>
            <label className="text-sm text-slate-700">Course Info Title
              <input type="text" value={editForm.courseInfoTitle} onChange={(e) => setEditForm((p) => ({ ...p, courseInfoTitle: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="text-sm text-slate-700">Course Info Subtitle
              <input type="text" value={editForm.courseInfoSubtitle} onChange={(e) => setEditForm((p) => ({ ...p, courseInfoSubtitle: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="text-sm text-slate-700">Date Text
              <input type="text" value={editForm.dateText} onChange={(e) => setEditForm((p) => ({ ...p, dateText: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="text-sm text-slate-700">Location Text
              <input type="text" value={editForm.locationText} onChange={(e) => setEditForm((p) => ({ ...p, locationText: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="text-sm text-slate-700">Format Text
              <input type="text" value={editForm.formatText} onChange={(e) => setEditForm((p) => ({ ...p, formatText: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="text-sm text-slate-700">Language Text
              <input type="text" value={editForm.languageText} onChange={(e) => setEditForm((p) => ({ ...p, languageText: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="text-sm text-slate-700">Seats Remaining Text
              <input type="text" value={editForm.seatsRemainingText} onChange={(e) => setEditForm((p) => ({ ...p, seatsRemainingText: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="text-sm text-slate-700">Contact Text
              <input type="text" value={editForm.contactText} onChange={(e) => setEditForm((p) => ({ ...p, contactText: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="text-sm text-slate-700">Display Order
              <input type="number" value={editForm.displayOrder} onChange={(e) => setEditForm((p) => ({ ...p, displayOrder: Number(e.target.value || 1) }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 mt-6">
              <input type="checkbox" checked={Boolean(editForm.isActive)} onChange={(e) => setEditForm((p) => ({ ...p, isActive: e.target.checked }))} />
              Active
            </label>
          </div>
          <div className="mt-3 flex justify-end">
            <button type="button" onClick={saveEdit} className="px-4 py-2 rounded-lg bg-[#011337] text-white hover:bg-[#011337]/90 text-sm">Save changes</button>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default TrainingsManager;
